import { css } from '@emotion/react';
import styled from '@emotion/styled';
import cns from 'classnames';
import { BaseHTMLAttributes, FC } from 'react';
import { space, SpaceProps } from 'styled-system';

export const AVATAR_CLASSNAME = '__mapui_avatar';

export type AvatarVariant = 'light' | 'dark';

export type AvatarSize = 'normal' | 'large';

export const AVATAR_VARIANT = {
  LIGHT: 'light',
  DARK: 'dark',
};

export const AVATAR_SIZE = {
  NORMAL: 'normal',
  LARGE: 'large',
};

export interface AvatarProps
  extends BaseHTMLAttributes<HTMLDivElement>,
    SpaceProps {
  className?: string;
  name: string;
  variant?: AvatarVariant;
  size?: AvatarSize;
}

const evaluateName = (name: string) => {
  if (name === undefined) {
    return '';
  }

  const words = name.split(' ');
  if (words.length > 1) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  return `${name[0]}${name[1]}`.toUpperCase();
};

const avatarStyle =
  ({ variant, size }) =>
  (theme) => {
    const defaultStyle = css(theme.avatar.defaultStyles);
    const variantStyle = css(theme.avatar[variant]);
    const sizeStyles = css(theme.avatar[size]);

    const styles = [defaultStyle, variantStyle, sizeStyles];

    return css(...styles);
  };

const AvatarContainer = styled.div<Omit<AvatarProps, 'name'>>(
  ({ theme, variant = 'dark', size = 'normal' }) => ({
    ...avatarStyle({ variant, size })(theme),
  }),
  ({ className }) => ({
    className: cns(AVATAR_CLASSNAME, className),
  }),
  space
);

export const Avatar: FC<AvatarProps> = ({ name, ...rest }: AvatarProps) => (
  <AvatarContainer {...rest}>{evaluateName(name)}</AvatarContainer>
);
